import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router"; 
import axios from "axios";
import Login from "./Login";
import Products from "./Product.js";
import Cart from "./Cart.js";
import Signup from "./Signup.js";
import About from "./About.js";
import SearchResults from "./SearchResults.js";
import ProductCategory from "./ProductCategory.js"
import ProductDetails from "./ProductDetails.js";
import Navbar from "../Components/Navbar.jsx";
import { SessionProvider } from "../Cart-Context/session.js"; 
import OrderComplete from "./OrderComplete.js";
import "../App.css";
import Checkout from "../Components/Checkout.js";
import PopupScheduler from "../Components/PopupScheduler.js";

import Research from "./ResearchPortal/Research.js";
import Account from "./ResearchPortal/Account.js";
import Surveys from "./ResearchPortal/Surveys.js";
import Data from "./ResearchPortal/Data.js";
import DataDetails from './ResearchPortal/DataDetails';
import AddRemoveProducts from "./ResearchPortal/AddRemoveProducts"; 
import ManagePopups from "./ResearchPortal/ManagePopups.js";
import Home from "./Home.js";

import { API_BASE_URL } from "../utils/apiConfig.js";

function App() {
  const navigate = useNavigate();
  const location = useLocation(); 

  const [currentUser, setCurrentUser] = useState(localStorage.getItem("user") || null);
  const [currentUserRole, setCurrentUserRole] = useState(localStorage.getItem("userRole") || null);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  // --- QUALTRICS JOIN CODE LOGIC ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');

    if (code) {
      const attemptJoin = async () => {
        // 1. SECURITY GATE: Block researchers
        const userRole = localStorage.getItem("userRole");
        if (userRole === 'researcher') {
          alert("Access Denied: Researchers cannot join experiments as participants.");
          navigate('/research-portal', { replace: true });
          return;
        }

        // 2. GET GUEST ID (Wait for SessionProvider to save to localStorage)
        const currentSessionId = localStorage.getItem("sessionId");

        if (!currentSessionId) {
          setTimeout(attemptJoin, 500); 
          return;
        }

        try {
          const res = await axios.post(`${API_BASE_URL}/api/join-experiment`, { 
            code, 
            sessionId: currentSessionId 
          });

          if (res.data.success) {
            // Save experiment info to localStorage so Cart/Navbar see it
            localStorage.setItem("experimentId", res.data.experimentId);
            localStorage.setItem("hiddenCategories", res.data.hiddenCategories || "[]");
            
            alert(`Success! Joined: ${res.data.experimentName}`);
            
            // This forces the app to re-read everything from localStorage
            // ensuring the restricted Navbar/Search bar kicks in immediately.
            window.location.href = window.location.origin + window.location.pathname;
          }
        } catch (err) {
          alert(`Access Denied: ${err.response?.data?.message || "Invalid Code"}`);
          navigate('/', { replace: true });
        }
      };

      attemptJoin();
    }
  }, [location.search, navigate]);
  
  // Update role state if localStorage changes (e.g., after login)
  useEffect(() => {
    const savedRole = localStorage.getItem('userRole'); 
    if (savedRole !== currentUserRole) {
      setCurrentUserRole(savedRole); 
    }
  }, [currentUser]);

  // Fetch Featured Products
  useEffect(() => {
      const fetchFeatured = async () => {
          const savedFeatured = sessionStorage.getItem('featuredProducts');
          if (savedFeatured) {
              setFeaturedProducts(JSON.parse(savedFeatured));
              return; 
          }
          try {
              const response = await fetch(`${API_BASE_URL}/api/featured-products`);
              const data = await response.json();
              if (data && data.length > 0) {
                  const mapped = data.map(p => {
                      const rawImage = p.image || p.productImage;
                      const finalImage = (rawImage && rawImage !== "autoFill.jpg")
                          ? `https://open-access-online-experimental-retail-facility-product-images.s3.us-east-2.amazonaws.com/images/${rawImage}`
                          : `https://open-access-online-experimental-retail-facility-product-images.s3.us-east-2.amazonaws.com/images/autoFill.jpg`;
                      return { ...p, id: p.productId || p.id, name: p.productName || p.name, image: finalImage };
                  });
                  setFeaturedProducts(mapped);
                  sessionStorage.setItem('featuredProducts', JSON.stringify(mapped));
              }
          } catch (err) {
              console.error("Error loading home products:", err);
          }
      };
    fetchFeatured();
  }, []);

  const handleLogin = (userEmail, role) => {
    setCurrentUser(userEmail);
    localStorage.setItem("user", userEmail);

    setCurrentUserRole(role);
    localStorage.setItem("userRole", role);
  };
  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentUserRole(null);
    localStorage.removeItem("user");
    localStorage.removeItem("userRole");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("cartId");
    localStorage.removeItem("experimentId");

    sessionStorage.clear();
    window.dispatchEvent(new Event("cartUpdated"));
    navigate('/');
  };

  return (
    <SessionProvider userRole={currentUserRole}>
      <PopupScheduler />
      <Navbar 
        currentUser={currentUser} 
        onLogout={handleLogout} 
        currentUserRole={currentUserRole} 
      />
      <Routes>
        <Route path="/" element={<Home featuredProducts={featuredProducts} />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/products" element={<Products />} />
        <Route path="/product/:id/*" element={<ProductDetails />} />
        <Route path="/products/aisle/:aisle" element={<ProductCategory />} />
        <Route path="/products/category/:category" element={<ProductCategory />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} /> 
        <Route path="/order-complete" element={<OrderComplete />} />
        <Route path="/about" element={<About />} />
        <Route path="/login" element={<Login onLoginSuccess={handleLogin} />} />
        <Route path="/signup" element={<Signup />} />

        {currentUserRole === 'researcher' && (
          <>
            <Route path="/research-portal" element={<Research />} />
            <Route path="/research-portal/data" element={<Data />} />
            <Route path="/research-portal/data/:sessionId" element={<DataDetails />} />
            <Route path="/research-portal/add-remove-products" element={<AddRemoveProducts />} />
            <Route path="/research-portal/surveys" element={<Surveys />} />
            <Route path="/research-portal/account" element={<Account />} />
            <Route path="/research-portal/manage-popups" element={<ManagePopups />} />
          </>
        )}
      </Routes>
    </SessionProvider>
  );
}

export default App;
